import { Link, useRouterState } from "@tanstack/react-router";
import { motion } from "motion/react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { navGroups } from "@/config/navigation";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

type SidebarProps = {
  collapsed: boolean;
  onToggle: () => void;
};

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { profile, user } = useAuth();
  const displayName = profile ? `${profile.fname} ${profile.lname}`.trim() : user?.email ?? "User";
  const initials =
    profile?.fname && profile?.lname
      ? `${profile.fname[0]}${profile.lname[0]}`.toUpperCase()
      : (user?.email?.[0] ?? "U").toUpperCase();

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 56 : 220 }}
      transition={{ type: "spring", stiffness: 380, damping: 36 }}
      className="flex h-full shrink-0 flex-col border-r border-sidebar-border bg-card text-sidebar-foreground"
    >
      <div
        className={cn(
          "flex h-12 items-center gap-2.5 border-b border-border px-3",
          collapsed && "justify-center px-0",
        )}
      >
        <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-[11px] font-bold text-primary-foreground">
          FS
        </div>
        {!collapsed ? (
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold tracking-[-0.01em]">Factory Sarathi</p>
            <p className="truncate text-[10px] text-muted-foreground">Manufacturing ERP</p>
          </div>
        ) : null}
      </div>

      {!collapsed ? (
        <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5">
          <Avatar className="size-8">
            <AvatarFallback className="bg-primary/10 text-[11px] font-semibold text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-xs font-medium">{displayName}</p>
            <p className="truncate text-[10px] text-muted-foreground">{user?.email}</p>
          </div>
        </div>
      ) : null}

      <ScrollArea className="flex-1 px-2 py-2">
        <nav className="space-y-3">
          {navGroups.map((group) => (
            <div key={group.title} className="space-y-px">
              {!collapsed ? (
                <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                  {group.title}
                </p>
              ) : (
                <div className="mx-2 my-2 border-t border-border" />
              )}

              {group.items.map((item) => {
                const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
                const Icon = item.icon;

                if (item.enabled === false) {
                  return (
                    <div
                      key={item.to}
                      title={`${item.title} — coming soon`}
                      className={cn(
                        "flex h-8 cursor-default items-center gap-2.5 rounded-md px-2.5 text-xs text-muted-foreground/40",
                        collapsed && "justify-center px-0",
                      )}
                    >
                      <Icon className="size-4 shrink-0" strokeWidth={1.75} />
                      {!collapsed ? <span className="truncate">{item.title}</span> : null}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    title={collapsed ? item.title : undefined}
                    className={cn(
                      "flex h-8 items-center gap-2.5 rounded-md px-2.5 text-xs transition-colors",
                      collapsed && "justify-center px-0",
                      active
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-foreground/75 hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <Icon className="size-4 shrink-0" strokeWidth={1.75} />
                    {!collapsed ? <span className="truncate">{item.title}</span> : null}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      </ScrollArea>

      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className="flex h-9 items-center justify-center gap-1.5 border-t border-border text-2xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        {collapsed ? <PanelLeftOpen className="size-3.5" /> : <PanelLeftClose className="size-3.5" />}
        {!collapsed ? "Collapse" : null}
      </button>
    </motion.aside>
  );
}
